import { Box, Button, Icon, Typography } from '@material-ui/core';
import React, { forwardRef } from 'react';
import { formatBytes } from '../../utils/byteFormats';

export interface IProps {
  accept: string[];
  children: React.ReactNode;
  disabled?: boolean;
  errorMessage?: string;
  icon?: string;
  label?: string;
  maxUpload?: number;
  multiple?: boolean;
  onClose?: () => unknown;
  onOpen?: (event: React.MouseEvent<HTMLButtonElement>) => unknown;
  onSelect?: (
    formData: FormData | undefined,
    e: React.ChangeEvent<HTMLInputElement>
  ) => unknown;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'contained' | 'outlined';
}

const FileUploader = forwardRef<HTMLInputElement, IProps>(
  (
    {
      accept,
      children,
      disabled,
      errorMessage,
      icon,
      label,
      maxUpload,
      multiple,
      onClose,
      onOpen,
      onSelect,
      size,
      variant,
    },
    ref
  ) => {
    function handleAbort() {
      window.removeEventListener('focus', handleAbort);

      if (typeof onClose === 'function') {
        return onClose();
      }
    }

    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      window.addEventListener('focus', handleAbort);

      (ref as React.MutableRefObject<HTMLInputElement>).current.click();

      if (typeof onOpen === 'function') {
        return onOpen(event);
      }
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      window.removeEventListener('focus', handleAbort);

      if (typeof onSelect === 'function') {
        const formInput = (ref as React.MutableRefObject<HTMLInputElement>)
          .current;

        if (!(formInput instanceof HTMLInputElement)) {
          return;
        }

        if (formInput.files == null) {
          return;
        }

        // TODO: Abstract to function
        if (formInput.files[0].size > maxUpload) {
          alert(`File size exceeds ${formatBytes(maxUpload, 0)} limit`);
          return;
        }

        const formData = new FormData();

        const file = formInput.files.item(0);

        formData.append('file', file, file.name);

        return onSelect(formData, event);
      }
    }

    return (
      <Box>
        <input
          accept={accept.join(',')}
          id="file"
          multiple={multiple}
          name={multiple ? 'files' : 'file'}
          onChange={handleChange}
          ref={ref}
          style={{ display: 'none' }}
          type="file"
        />
        <Button
          disabled={disabled}
          onClick={handleClick}
          size={size || 'medium'}
          startIcon={<Icon>{icon || 'file_upload'}</Icon>}
          variant={variant || 'text'}
        >
          {label || 'Upload'}
          {children}
        </Button>
        {errorMessage !== undefined && (
          <Box color="error.main">
            <Typography variant="body1" component="small">
              {errorMessage}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
);

FileUploader.displayName = 'FileUploader';

export { FileUploader };
